import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RemoveShoppingCartOutlinedIcon from "@mui/icons-material/RemoveShoppingCartOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ClientAutocomplete from "../clientAutocomplete";
import SettingsSection, { settingsButtonSx, settingsText } from "../settings-section";
import SalesDashboardSetting, { SALES_DASHBOARD_KEY } from "../sales-dashboard-setting";
import LostSalesSettings from "../lost-sales-settings";
import InventoryRiskSettings from "../inventory-risk-settings";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { SECTION_KPI } from "../metrics/salesDashboardConfig";
import {
  INVENTORY_RISK_SETTINGS_KEY,
  bandsFromRow,
  describeRiskBands,
} from "../../utils/inventoryRiskSettings";
import {
  LOST_SALES_SETTINGS_KEY,
  describeLostSalesParameters,
} from "../../utils/lostSalesSettings";

const SALES_SECTION_COUNT = 4;

/**
 * Configurable dashboards, in display order. Each entry owns its clientConfig
 * key, a one-line summary of a saved row, and the editor shown in the dialog.
 * Adding a dashboard here is the only change this card needs.
 */
export const CONFIGURABLE_DASHBOARDS = [
  {
    id: "sales_over_time",
    name: "Sales over Time",
    description: "Which sections and KPI tiles the dashboard shows.",
    icon: InsightsOutlinedIcon,
    configKey: SALES_DASHBOARD_KEY,
    dialogWidth: "md",
    Editor: SalesDashboardSetting,
    summarize: (row) => {
      if (!row) return "All sections and the standard KPI tiles.";
      const sections = row.value?.sections || {};
      const shown = Object.values(sections).filter((section) => section?.enabled).length;
      const kpis = sections[SECTION_KPI]?.enabled
        ? ` · ${sections[SECTION_KPI].metrics?.length || 0} KPI tiles`
        : " · KPI summary hidden";
      return `${shown} of ${SALES_SECTION_COUNT} sections shown${kpis}`;
    },
  },
  {
    id: "lost_sales",
    name: "Lost Sales Analysis",
    description:
      "How expected demand is estimated: search window, day-of-week adjustment and safe period.",
    icon: RemoveShoppingCartOutlinedIcon,
    configKey: LOST_SALES_SETTINGS_KEY,
    dialogWidth: "sm",
    Editor: LostSalesSettings,
    summarize: (row) =>
      row
        ? describeLostSalesParameters(row.value?.parameters)
            .map(({ label }) => label)
            .join(" · ")
        : "Standard calculation settings.",
  },
  {
    id: "inventory_risk",
    name: "Stock risk categories",
    description:
      "Days-of-cover limits for Critical, At Risk and the other categories — used by Inventory Risk, Purchase Order Analysis and the Command Center.",
    icon: ReportProblemOutlinedIcon,
    configKey: INVENTORY_RISK_SETTINGS_KEY,
    dialogWidth: "sm",
    Editor: InventoryRiskSettings,
    summarize: (row) => (row ? describeRiskBands(bandsFromRow(row)) : "Standard categories."),
  },
];

const DashboardRow = ({ dashboard, row, loading, onOpen }) => {
  const Icon = dashboard.icon;
  const customized = Boolean(row);
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`${dashboard.name} settings`}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.75,
        textAlign: "left",
        borderRadius: "10px",
        border: "1px solid var(--border-color)",
        transition: "background-color 120ms, border-color 120ms",
        "&:hover": {
          backgroundColor: "action.hover",
          borderColor: "var(--text-secondary)",
        },
        "&.Mui-focusVisible": { outline: "2px solid var(--accent)", outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "9px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          bgcolor: "action.selected",
          color: "var(--text-primary)",
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ ...settingsText.value, fontWeight: 600 }}>
          {dashboard.name}
        </Typography>
        <Typography sx={{ ...settingsText.muted, mt: 0.25 }}>
          {dashboard.description}
        </Typography>
        <Typography
          noWrap
          data-testid={`dashboard-summary-${dashboard.id}`}
          sx={{ ...settingsText.muted, fontSize: 12, mt: 0.5, color: "var(--text-primary)" }}
        >
          {loading ? "Loading…" : dashboard.summarize(row)}
        </Typography>
      </Box>
      {!loading && (
        <Chip
          size="small"
          label={customized ? "Customized" : "Standard"}
          color={customized ? "primary" : "default"}
          variant="outlined"
          sx={{ fontFamily: "poppins", fontSize: 11.5, flexShrink: 0 }}
        />
      )}
      <ChevronRightIcon sx={{ color: "var(--text-secondary)", flexShrink: 0 }} />
    </ButtonBase>
  );
};

/**
 * Client Config → Dashboard settings: one card listing every configurable
 * dashboard; each opens its own settings in a dialog. On Client Config it
 * edits the signed-in client (`clientId`); without `clientId` (Admin Config)
 * it shows a client picker.
 */
const DashboardSettings = ({ clientId: fixedClientId = null }) => {
  const [pickedClient, setPickedClient] = useState(null);
  const clientId = fixedClientId || pickedClient?._id || null;
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setLoadError("");
    try {
      const results = await Promise.all(
        CONFIGURABLE_DASHBOARDS.map((dashboard) =>
          clientConfigService.fetch(clientId, 1, 0, dashboard.configKey),
        ),
      );
      setRows(
        Object.fromEntries(
          CONFIGURABLE_DASHBOARDS.map((dashboard, index) => [
            dashboard.id,
            (results[index]?.data || []).find((r) => r.key === dashboard.configKey) || null,
          ]),
        ),
      );
    } catch (error) {
      setLoadError(
        error?.response?.data?.message || "Couldn't load the dashboard settings.",
      );
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    setRows({});
    setOpenId(null);
    load();
  }, [load]);

  const open = CONFIGURABLE_DASHBOARDS.find((dashboard) => dashboard.id === openId);
  const Editor = open?.Editor;
  const close = () => setOpenId(null);

  return (
    <SettingsSection
      testId="dashboard-settings"
      title="Dashboard settings"
      description="Tailor analytics dashboards for this client. Changes apply to everyone viewing the client's dashboards."
    >
      {!fixedClientId && (
        <Box sx={{ mb: clientId ? 2 : 0 }}>
          <ClientAutocomplete
            selectedClient={pickedClient}
            onClientChange={(next) => setPickedClient(next || null)}
            width={320}
            margin="none"
          />
        </Box>
      )}

      {!clientId && (
        <Typography sx={{ ...settingsText.muted, mt: 1.5 }}>
          Select a client to view or edit its dashboard settings.
        </Typography>
      )}

      {clientId && loadError && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={settingsText.muted}>{loadError}</Typography>
          <Button size="small" onClick={load} sx={settingsButtonSx}>
            Retry
          </Button>
        </Box>
      )}

      {clientId && !loadError && (
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {CONFIGURABLE_DASHBOARDS.map((dashboard) => (
            <DashboardRow
              key={dashboard.id}
              dashboard={dashboard}
              row={rows[dashboard.id]}
              loading={loading}
              onOpen={() => setOpenId(dashboard.id)}
            />
          ))}
        </Box>
      )}

      <Dialog
        open={Boolean(open)}
        onClose={close}
        fullWidth
        maxWidth={open?.dialogWidth || "sm"}
        aria-labelledby="dashboard-settings-dialog-title"
        PaperProps={{ sx: { borderRadius: "12px", backgroundColor: "var(--bg-paper)" } }}
      >
        {open && (
          <>
            <DialogTitle
              id="dashboard-settings-dialog-title"
              sx={{ display: "flex", alignItems: "flex-start", gap: 2, pb: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography component="span" sx={{ ...settingsText.title, display: "block" }}>
                  {open.name}
                </Typography>
                <Typography component="span" sx={{ ...settingsText.description, display: "block" }}>
                  {open.description}
                </Typography>
              </Box>
              <IconButton aria-label="Close" onClick={close} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: "var(--border-color)" }}>
              {clientId ? (
                <Editor
                  clientId={clientId}
                  onCancel={close}
                  onSaved={() => {
                    close();
                    load();
                  }}
                />
              ) : (
                <CircularProgress size={16} />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </SettingsSection>
  );
};

export default DashboardSettings;
